import {use, useCallback, useMemo, useRef, useState} from 'react'
import {Comments} from '../comments/Comments'
import {CommentsContext, ViewMode} from '../comments/CommentsContext'
import {CommentsViewControls} from '../comments/CommentsViewControls'
import type {IssueCommentComposerHandle} from '../comments/IssueCommentComposer'
import {useFilteredCommentsData} from '../comments/use-filtered-comments-data'
import {useLayoutAuthorLogin} from '../../stores/layout-selectors'
import {usePullRequestBasePath} from '../../stores/selectors'
import styles from './CommentsSection.module.css'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useCommentsFilterState} from '../comments/use-comments-filter-state'

export function CommentsSection() {
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.IssueComments)

  const commentsContextValue = useMemo(() => ({viewMode, setViewMode}), [viewMode, setViewMode])

  return (
    <section className={styles.container}>
      <ErrorBoundary
        // TODO: Improve error fallback
        fallback={
          <>
            <h2 className={styles.heading}>Comments</h2>
            <p>Failed to load comments. Please refresh the page to try again.</p>
          </>
        }
        boundaryName="OverviewCommentsSection"
      >
        <CommentsContext value={commentsContextValue}>
          <CommentsSectionContent />
        </CommentsContext>
      </ErrorBoundary>
    </section>
  )
}

function CommentsSectionContent() {
  const pathName = usePullRequestBasePath()
  const pullRequestAuthorLogin = useLayoutAuthorLogin()

  const viewMode = use(CommentsContext).viewMode
  const {filterState, setCurrentFilterState} = useCommentsFilterState(viewMode)
  const {
    authorList,
    filteredConversationItems,
    selectedReviewId,
    totalConversationItemsCount,
    filterStateWithOverrides,
  } = useFilteredCommentsData({filterState})

  const composerRef = useRef<IssueCommentComposerHandle>(null)
  const handleQuoteReply = useCallback((quotedText: string) => composerRef.current?.insertQuote(quotedText), [])

  return (
    <>
      <div className={styles.controlsRow}>
        <h2 className={styles.heading}>Comments</h2>
        <CommentsViewControls
          authorList={authorList}
          filterState={filterState}
          setFilterState={setCurrentFilterState}
        />
      </div>
      <Comments
        filteredConversationItems={filteredConversationItems}
        selectedReviewId={selectedReviewId}
        handleQuoteReply={handleQuoteReply}
        pathName={pathName}
        pullRequestAuthorLogin={pullRequestAuthorLogin}
        totalConversationItemsCount={totalConversationItemsCount}
        filterStateWithOverrides={filterStateWithOverrides}
        composerRef={composerRef}
      />
    </>
  )
}
